import { useState, useEffect, useRef } from "react";
import { IoPencil, IoTrash, IoAdd, IoCheckmark, IoClose } from "react-icons/io5";
import { useNotificationContext } from "context/NotificationContext";
import { handleComponentSave } from "./saveHelper";
import { addLeadSchedule, editLeadSchedule, deleteLeadSchedule } from "../../tables/leadApi";
import { usePermission } from "hooks/usePermission";

const Schedule = ({ lead, updateLead, showToast, setCurrentLead }) => {
  const { refreshNotifications } = useNotificationContext();
  const { can } = usePermission();
  const canCreateLead = can("lead_management", "create");
  const canUpdateLead = can("lead_management", "update");
  const canDeleteLead = can("lead_management", "delete");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedSchedules, setEditedSchedules] = useState([]);
  const [originalSchedules, setOriginalSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    scheduledTime: "",
    note: "",
  });
  const [isAddingSchedule, setIsAddingSchedule] = useState(false);
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null });
  const [showEditStatusDropdown, setShowEditStatusDropdown] = useState(null);
  const editStatusDropdownRef = useRef(null);

  // Initialize schedules on mount
  useEffect(() => {
    const schedules = lead?.schedules || [];
    setEditedSchedules(schedules);
    setOriginalSchedules(schedules);
  }, [lead]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        editStatusDropdownRef.current &&
        !editStatusDropdownRef.current.contains(event.target)
      ) {
        setShowEditStatusDropdown(null);
      }
    };

    if (showEditStatusDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showEditStatusDropdown]);

  const handleAddSchedule = async () => {
    // client-side validation
    const errors = {};
    if (!newSchedule.scheduledTime) errors.scheduledTime = "Scheduled Time is required";
    if (!newSchedule.note || !newSchedule.note.trim()) errors.note = "Note is required";
    if (Object.keys(errors).length > 0) {
      setScheduleErrors(errors);
      return;
    }

    setIsAddingSchedule(true);
    setScheduleErrors({});
    try {
      const payload = {
        leadId: lead?._id,
        scheduledTime: new Date(newSchedule.scheduledTime).toISOString(),
        note: newSchedule.note.trim(),
        scheduleStatus: "scheduled",
      };
      const response = await addLeadSchedule(payload);

      // If server returns the full schedules array, use it as the single source of truth.
      if (response?.data?.schedules && Array.isArray(response.data.schedules)) {
        setEditedSchedules(response.data.schedules);
      } else {
        // normalize created schedule from response
        const createdSchedule = response?.data?.schedule || response?.schedule || response?.data || response;

        // if API returned a simple message or no schedule, fall back to constructing one
        const finalSchedule = typeof createdSchedule === "object" ? createdSchedule : {
          ...newSchedule,
          scheduleStatus: payload.scheduleStatus,
          createdAt: new Date().toISOString(),
          scheduledTime: payload.scheduledTime,
          _id: `temp_${Date.now()}`,
        };

        setEditedSchedules((prev) => [finalSchedule, ...prev]);
      }
      setNewSchedule({ scheduledTime: "", note: "" });
      setIsModalOpen(false);
      showToast(response?.message || "Schedule added successfully", "success");
      await refreshNotifications();
    } catch (err) {
      // handle API validation errors
      const apiErrors = err?.response?.data?.errors || err?.response?.data || null;
      if (apiErrors && typeof apiErrors === "object") {
        // map to field keys
        const mapped = {};
        if (apiErrors.scheduledTime) mapped.scheduledTime = apiErrors.scheduledTime;
        if (apiErrors.note) mapped.note = apiErrors.note;
        if (Object.keys(mapped).length > 0) {
          setScheduleErrors(mapped);
        } else if (apiErrors.message) {
          setScheduleErrors({ submit: apiErrors.message });
        } else {
          showToast("Failed to add schedule", "error");
        }
      } else {
        showToast("Failed to add schedule", "error");
      }
    } finally {
      setIsAddingSchedule(false);
    }
  };

  const handleDeleteSchedule = (index) => {
    // show confirmation modal before deleting
    setDeleteConfirm({ open: true, index });
  };

  useEffect(() => {
    if (deleteConfirm.open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
    return undefined;
  }, [deleteConfirm.open]);

  const handleEditSchedule = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = async (index) => {
    const updatedSchedule = editedSchedules[index];
    if (!updatedSchedule.scheduledTime || !updatedSchedule.note) {
      showToast("Please fill in all fields", "error");
      return;
    }

    const scheduleId = updatedSchedule._id || updatedSchedule.scheduleId || updatedSchedule.id;

    if (scheduleId && !String(scheduleId).startsWith("temp_")) {
      try {
        const payload = {
          leadId: lead?._id,
          scheduleId,
          scheduledTime: new Date(updatedSchedule.scheduledTime).toISOString(),
          note: updatedSchedule.note,
          scheduleStatus: updatedSchedule.scheduleStatus || "scheduled",
        };

        const response = await editLeadSchedule(payload);
        if (response?.data?.schedules && Array.isArray(response.data.schedules)) {
          setEditedSchedules(response.data.schedules);
        } else {
          const saved = response?.data?.schedule || response?.schedule || response?.data || response;
          const finalSchedule = typeof saved === "object" ? saved : { ...updatedSchedule, scheduleStatus: payload.scheduleStatus, scheduledTime: payload.scheduledTime };

          setEditedSchedules((prev) => prev.map((s, i) => (i === index ? finalSchedule : s)));
        }
        showToast(response?.message || "Schedule updated", "success");
      } catch (err) {
        const apiErrors = err?.response?.data?.errors || err?.response?.data || null;
        if (apiErrors && typeof apiErrors === "object") {
          const mapped = {};
          if (apiErrors.scheduledTime) mapped.scheduledTime = apiErrors.scheduledTime;
          if (apiErrors.note) mapped.note = apiErrors.note;
          if (Object.keys(mapped).length > 0) {
            setScheduleErrors(mapped);
          } else if (apiErrors.message) {
            showToast(apiErrors.message, "error");
          } else {
            showToast("Failed to update schedule", "error");
          }
        } else {
          showToast("Failed to update schedule", "error");
        }
      } finally {
        setEditingIndex(null);
      }
    } else {
      // local-only schedule (not saved on server yet)
      setEditedSchedules((prev) => {
        const copy = [...prev];
        copy[index] = updatedSchedule;
        return copy;
      });
      setEditingIndex(null);
    }
  };

  const performDelete = async () => {
    const index = deleteConfirm.index;
    if (index == null) return setDeleteConfirm({ open: false, index: null });

    const schedule = editedSchedules[index];
    const scheduleId = schedule?._id || schedule?.scheduleId || schedule?.id;

    // close modal immediately
    setDeleteConfirm({ open: false, index: null });
    document.body.style.overflow = "";

    if (!scheduleId || String(scheduleId).startsWith("temp_")) {
      // local only - just remove
      setEditedSchedules((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setDeletingIndex(index);
    try {
      const response = await deleteLeadSchedule(lead?._id, scheduleId);
      if (response?.data?.schedules && Array.isArray(response.data.schedules)) {
        setEditedSchedules(response.data.schedules);
      } else {
        setEditedSchedules((prev) => prev.filter((_, i) => i !== index));
      }
      showToast(response?.message || "Schedule deleted", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete schedule", "error");
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleScheduleChange = (index, field, value) => {
    const updatedSchedules = [...editedSchedules];
    updatedSchedules[index] = {
      ...updatedSchedules[index],
      [field]: value,
    };
    setEditedSchedules(updatedSchedules);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await handleComponentSave(
        lead,
        { schedules: editedSchedules },
        updateLead,
        showToast,
        setCurrentLead
      );
      if (result && result.success) {
        setIsEditing(false);
        setOriginalSchedules(editedSchedules);
        setScheduleErrors({});
      } else if (result && result.errors) {
        setScheduleErrors(result.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedSchedules(originalSchedules);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="flex w-full flex-col rounded-lg bg-white p-6 dark:bg-navy-800">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          Schedules
        </h3>
        <div className="flex items-center gap-2">
          {canCreateLead && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-1 text-sm text-white transition-colors hover:bg-brand-600"
              title="Add schedule"
            >
              <IoAdd className="text-lg" />
              Add
            </button>
          )}

          {!isEditing ? (
            canUpdateLead && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-1 text-sm text-white transition-colors hover:bg-brand-600"
                title="Edit schedules"
              >
                <IoPencil className="text-lg" />
              </button>
            )
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center gap-2 rounded-lg bg-gray-500 px-3 py-1 text-sm text-white transition-colors hover:bg-gray-600 disabled:opacity-60"
              >
                <IoClose className="text-lg" />
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 rounded-lg bg-green-500 px-3 py-1 text-sm text-white transition-colors hover:bg-green-600 disabled:opacity-60"
              >
                <IoCheckmark className="text-lg" />
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Add Schedule Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onMouseDown={(e) => e.stopPropagation()}>
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-navy-800">
            <h2 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
              Add Schedule
            </h2>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Scheduled Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={newSchedule.scheduledTime}
                  onChange={(e) => {
                    setNewSchedule({ ...newSchedule, scheduledTime: e.target.value });
                    setScheduleErrors((prev) => ({ ...prev, scheduledTime: null }));
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                />
                {scheduleErrors.scheduledTime && (
                  <p className="mt-1 text-xs text-red-500">{scheduleErrors.scheduledTime}</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Note <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newSchedule.note}
                  onChange={(e) => {
                    setNewSchedule({ ...newSchedule, note: e.target.value });
                    setScheduleErrors((prev) => ({ ...prev, note: null }));
                  }}
                  rows="3"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                  placeholder="Enter schedule note..."
                />
                {scheduleErrors.note && (
                  <p className="mt-1 text-xs text-red-500">{scheduleErrors.note}</p>
                )}
              </div>

            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleAddSchedule}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
              >
                {isAddingSchedule ? (
                  <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <IoCheckmark className="text-lg" />
                )}
                <span className="ml-1">Add</span>
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setNewSchedule({
                    scheduledTime: "",
                    note: "",
                    scheduleStatus: "scheduled",
                  });
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
              >
                <IoClose className="text-lg" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete confirmation modal */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-navy-800">
            <h2 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">Delete Schedule?</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">This schedule entry will be permanently removed. Are you sure?</p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={performDelete}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
              >
                {deletingIndex != null ? (
                  <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <IoTrash className="text-lg" />
                )}
                <span className="ml-1">Delete</span>
              </button>
              <button
                onClick={() => {
                  setDeleteConfirm({ open: false, index: null });
                  document.body.style.overflow = "";
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
              >
                <IoClose className="text-lg" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedules List with Scroll */}
      <div className="max-h-96 space-y-3 overflow-y-auto">
        {editedSchedules && editedSchedules.length > 0 ? (
          editedSchedules.map((schedule, index) => (
            <div
              key={schedule._id || schedule.scheduleId || schedule.id || index}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-navy-700"
            >
              {editingIndex === index && isEditing ? (
                // Edit Mode
                <div className="space-y-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                      Scheduled Time
                    </label>
                    <input
                      type="datetime-local"
                      value={schedule.scheduledTime?.split(".")[0] || ""}
                      onChange={(e) =>
                        handleScheduleChange(index, "scheduledTime", e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                      Note
                    </label>
                    <textarea
                      value={schedule.note || ""}
                      onChange={(e) =>
                        handleScheduleChange(index, "note", e.target.value)
                      }
                      rows="2"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                      Status
                    </label>
                    <div className="relative" ref={editStatusDropdownRef}>
                      <button
                        onClick={() =>
                          setShowEditStatusDropdown(
                            showEditStatusDropdown === index ? null : index
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-left text-navy-700 transition hover:border-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                      >
                        {schedule.scheduleStatus || "scheduled"}
                      </button>
                      {showEditStatusDropdown === index && (
                        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                          {["scheduled", "completed", "cancelled"].map((status) => (
                            <div
                              key={status}
                              onClick={() => {
                                handleScheduleChange(index, "scheduleStatus", status);
                                setShowEditStatusDropdown(null);
                              }}
                              className="cursor-pointer px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            >
                              <p className="text-sm font-medium text-navy-700 capitalize dark:text-white">
                                {status}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleSaveEdit(index)}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-500 px-3 py-2 text-sm text-white transition-colors hover:bg-green-600"
                    >
                      <IoCheckmark className="text-lg" />
                      Done
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-500 px-3 py-2 text-sm text-white transition-colors hover:bg-gray-600"
                    >
                      <IoClose className="text-lg" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <h4 className="font-semibold text-navy-700 dark:text-white">
                        {formatDate(schedule.scheduledTime)}
                      </h4>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${schedule.scheduleStatus === "completed"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : schedule.scheduleStatus === "cancelled"
                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                          }`}
                      >
                        {schedule.scheduleStatus}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {schedule.note}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                      Created: {formatDate(schedule.createdAt)}
                    </p>
                  </div>

                  <div className="ml-3 flex gap-2">
                    {canUpdateLead && (
                      <button
                        onClick={() => isEditing && handleEditSchedule(index)}
                        className={`rounded-lg p-2 text-white transition-colors ${isEditing
                          ? "bg-brand-500 hover:bg-brand-600 cursor-pointer"
                          : "bg-gray-400 cursor-not-allowed opacity-50"
                          }`}
                        title="Edit"
                        disabled={!isEditing}
                      >
                        <IoPencil className="text-base" />
                      </button>
                    )}
                    {canDeleteLead && (
                      <button
                        onClick={() => isEditing && handleDeleteSchedule(index)}
                        className={`rounded-lg p-2 text-white transition-colors ${isEditing
                          ? "bg-red-500 hover:bg-red-600 cursor-pointer"
                          : "bg-gray-400 cursor-not-allowed opacity-50"
                          }`}
                        title="Delete"
                        disabled={!isEditing}
                      >
                        {deletingIndex === index ? (
                          <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <IoTrash className="text-base" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            No schedules yet
          </p>
        )}
      </div>
    </div>
  );
};

export default Schedule;

